import {BatchSchema} from 'constants/SampleConstants';
import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {CidIdMap, ContainedServerIdsFromClipboard} from 'notebook/notes/NoteConstants';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {
  AnyStructuredTable,
  BaseNotePart,
  NoteLinkedObjectType,
  NotePart,
  ServerNotePart,
  ToServerNotePart,
} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {MaybePromise} from 'util/Promises';

// The list of allowed NoteLinkedGroup kinds.  This should be in sync with the backend.
// TODO(BNCH-13934): add link to file where the backend model is
export enum NoteLinkedGroupKind {
  ANALYSIS_TEMPLATE_GROUP = 'ANALYSIS_TEMPLATE_GROUP',
  MIXTURE_GROUP = 'MIXTURE_GROUP',
  NOTE_PART_GRID = 'NOTE_PART_GRID',
  STOICHIOMETRY_GROUP = 'STOICHIOMETRY_GROUP',
}

/**
 * A NoteLinkedGroup is a container of several NotePartItem.
 */
export type BaseNoteLinkedGroup<TIds, TGroupMetadata> = {
  type: NoteLinkedObjectType.NOTE_LINKED_GROUP;

  // The name of the group which is used in the header of the component.
  name: string;

  // Every NoteLinkedGroup has a kind which informs the lifecycle calls.
  kind: NoteLinkedGroupKind;

  // A UUID which can be used to track a NoteLinkedGroup across all versions of the entry.
  provenanceUuid: string;

  // Will be true if the NoteLinkedGroup is being used in a template.
  isConfig: boolean;

  // An extensible metadata that can be specified per Kind of NoteLinkedGroup.
  groupMetadata: TGroupMetadata;

  // The list of NoteParts which are contained in the group.
  containedNoteParts: Array<BaseNotePart<TIds>>;
} & NLOId<TIds>;

export type NoteLinkedGroup<TGroupMetadata> = BaseNoteLinkedGroup<ClientId, TGroupMetadata>;
export type UnknownNoteLinkedGroup = NoteLinkedGroup<unknown>;

export type ToServerNoteLinkedGroup<TGroupMetadata> = BaseNoteLinkedGroup<ToServerId, TGroupMetadata> & {
  containedNoteParts: Array<ToServerNotePart>;
};
export type FromServerNoteLinkedGroup<TGroupMetadata> = BaseNoteLinkedGroup<FromServerId, TGroupMetadata> & {
  containedNoteParts: Array<ServerNotePart>;
};

// An interview for what props are needed to create a NoteLinkedGroup.
export interface CreateNoteLinkedGroupProps<TGroupMetadata, TExtras> {
  // The name for the NoteLinkedGroup
  name: string;

  // The particular kind of NoteLinkedGroup that we want to create.
  kind: NoteLinkedGroupKind;

  // All of the schemas which will be used by its children
  schemas: Array<Schema | BatchSchema>;

  // The API id of the entry or template that we are creating the NoteLinkedGroup within.
  entryId: string;

  // Additional metadata that's needed for creating a NoteLinkedGroup
  groupMetadata: TGroupMetadata;

  // Selectors for any schema fields used in the NoteLinkedGroup
  selectorsById: {[selectorId: string]: SchemaFieldSelector};

  // Any additional params that are necessary for creating a particular group; useful for passing data along
  // to createNoteParts that is needed for creating an individual note part.
  extras: TExtras;

  // Is the NLG rendered inside a config document or template?
  isConfig?: boolean;
}

// User-facing copy for the resync banner rendered inside a NoteLinkedGroup when its persisted
// state is detectably out-of-sync. The banner is shared across group kinds; adapters provide
// kind-specific wording via {@link NoteLinkedGroupAdapter.getResyncBannerContent}.
export interface NoteLinkedGroupResyncBannerContent {
  title: string;
  subtitle: string;
  buttonText: string;
}

export interface NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras> {
  kind: NoteLinkedGroupKind;

  // Whether this NoteLinkedGroup supports copy and pasting
  supportsCopyPaste: () => boolean;

  // Whether this group may be pasted into a DIFFERENT entry/template/document than it was copied
  // from. Loosens only the entry boundary; the cross-TENANT guard (Editor.itemFromHTML) is separate
  // and always enforced. Distinct from supportsCopyPaste (the duplicate-copy guard). Defaults to false.
  supportsCrossEntryPaste: () => boolean;

  // The name which we should show for this group
  getDisplayName: (noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>) => string;

  // The GroupActionFunctions which should be shown in the header of the group.
  getActionFunctions(): GroupActionFunctions<TGroupMetadata, TCreateGroupExtras>;

  // This function will be called when a group is created from the insertion modal.
  createGroup(createProps: CreateNoteLinkedGroupProps<TGroupMetadata, TCreateGroupExtras>): Omit<
    NoteLinkedGroup<TGroupMetadata>,
    'cid' | 'containedNoteParts'
  > & {
    containedNoteParts: Array<Omit<NotePart, 'cid'>>;
  };

  // Any updates of the contained NoteParts will travel through this handler.
  // The adapter can then respond to the updates of one particular item with a change
  // to the other contained items. The particular NotePart which is being updated can be
  // identified by the `cid` of the `item` which should then match to the `cid` of the same
  // item in the `noteLinkedGroup`.
  reduceGroup: (
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    item: NotePart
  ) => MaybePromise<NoteLinkedGroup<TGroupMetadata>>;

  // Optional group-level adaptation that runs after contained note parts are loaded into the redux
  // store shape. Override this for synchronous, in-memory repairs that need access to multiple
  // contained note parts; this must not dispatch editor mutations or trigger a save.
  prepareGroupForStore(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): void;

  // Returns true if the group's state is out of sync across its sub-components. Called during render.
  // Useful for detecting sync issues related to changes to the group's evaluated table data.
  isGroupResyncRequired(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    evaluatedTableMap: OutputTableMap
  ): boolean;

  // Repairs the persisted state of `noteLinkedGroup` so that it matches the latest evaluator
  // output reflected in `evaluatedTableMap`. Implementations should propagate any change that
  // {@link isGroupResyncRequired} would report as out-of-sync, and otherwise return the input
  // group unchanged. May be asynchronous when the repair requires fetching additional data
  // (e.g. loading a newly-selected parent mixture's ingredients). Defaults to a no-op for
  // group kinds that have no cross-table invariants to repair.
  resyncGroupComponentsAsync(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    evaluatedTableMap: OutputTableMap
  ): MaybePromise<NoteLinkedGroup<TGroupMetadata>>;

  // Returns the user-facing copy used by the resync banner that surfaces when
  // {@link isGroupResyncRequired} is true. Adapters that opt into the resync flow override this
  // to provide kind-specific wording (e.g. mixture-aware copy referencing ingredient rows).
  getResyncBannerContent(): NoteLinkedGroupResyncBannerContent;

  getContainedStructuredTables(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<AnyStructuredTable>;

  // Returns whether deleting the note part that contains this group should be allowed.
  // This allows group kinds to prevent deletion when any contained note part has protected state
  // (e.g. submitted rows in contained structured tables).
  allowDeleteGroup(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): {
    canDelete: boolean;
    reason?: string;
  };

  // Returns true if inserting or removing a note part of this group type should trigger a full table
  // evaluation pass (e.g. because the group contains formula-bearing structured tables). Defaults to false.
  requiresTableEvaluationOnInsert(): boolean;

  // Returns true if this group's contained tables should participate in the entry-level spreadsheet
  // evaluation cycle (enabling cross-table cell references with tables outside the group). When false,
  // the group's contained note parts are hidden from the evaluator, from entry-level Ctrl-F search over
  // cell values, and from the evaluated-value props threaded to contained tables, so such a group cannot
  // contain tables that need evaluation. Defaults to false.
  supportsEntryLevelEvaluation(): boolean;

  // The contained noteParts of a group are already viable sample sources. With this method, the group as a whole
  // can provide samples for propagation as well.
  getGroupSampleSources(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    evaluatedTableMap: OutputTableMap | null
  ): Array<SamplesSource>;

  getGroupSchemas(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<Schema>;
}

export type UnknownNoteLinkedGroupAdapter = NoteLinkedGroupAdapter<unknown, unknown>;

// GroupActionFunctions are ActionFunctions that operate on an entire NoteLinkedGroup.
export interface GroupActionFunctions<TGroupMetadata, TCreateGroupExtras> {
  primary?: ToolbarButtonGroupActionFunction<TGroupMetadata, TCreateGroupExtras>;
  secondary?: Array<ToolbarButtonGroupActionFunction<TGroupMetadata, TCreateGroupExtras>>;
  overflow?: Array<OverflowMenuGroupActionFunction<TGroupMetadata, TCreateGroupExtras>>;
  footer?: Array<ToolbarButtonGroupActionFunction<TGroupMetadata, TCreateGroupExtras>>;
}

export interface GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras> {
  entryOrTemplateId: string;
  noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>;
  evaluatedTableMap: OutputTableMap;
  actionFunctionContext: ActionFunctionContext;
  noteLinkedGroupAdapter: NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras>;
  locked: boolean;
  getIdFromCidFn: (id: string) => string | number | undefined;
  getItemWithUpdatedContent: (oldContainedNotePart: NotePart, content: object) => NotePart;
  updateGroup: (noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>) => void;
  insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => void;
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
  isCollapsed: boolean;
  groupResyncRequired: boolean;
}

export type GroupActionFunction<TGroupMetadata, TCreateGroupExtras> =
  | ToolbarButtonGroupActionFunction<TGroupMetadata, TCreateGroupExtras>
  | OverflowMenuGroupActionFunction<TGroupMetadata, TCreateGroupExtras>;

export interface ToolbarButtonGroupActionFunction<TGroupMetadata, TCreateGroupExtras> {
  renderToolbarButton: (
    params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
  ) => JSX.Element | null;
}

export interface OverflowMenuGroupActionFunction<TGroupMetadata, TCreateGroupExtras> {
  getOverflowMenuItem: (
    params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
  ) => OverflowMenuOption | null;
}

// SpreadsheetStore state is proxied through NoteEditor props down to the NoteLinkedGroup,
// which will ensure double-clicking a cell will cause that table to enter "edit" mode.
export interface StructuredTableSpreadsheetProps {
  metadataForRestore: MetadataForRestore<TableIndex> | null;
}
export interface NoteLinkedGroupItemProps {
  spreadsheetProps: StructuredTableSpreadsheetProps;
  // Resolved server IDs for contained note parts (derived from cidIdMap via
  // getIdFromCid). Included so that MediocreItem's deep-equal check on
  // itemProps detects the change after a save and re-renders the group.
  resolvedContainedItemIds?: Array<string | number | undefined>;
  // Computed table values for the NLG's contained tables. Included so that
  // MediocreItem's deep-equal check on itemProps detects when cross-group
  // cell references cause the NLG's evaluated values to change.
  containedComputedTables?: OutputTableMap;
  editingTable?: string | null;
}

/**
 * Transient carrier for a cross-entry-capable group's `{containedFileCid -> serverId}` map. It
 * bridges paste conversion with sources whose contained server IDs are not yet available through
 * the destination store:
 *
 *   1. `NoteLinkedGroupPlugin.itemFromHTML` parses the map off the clipboard HTML and `stash`es it
 *      onto the in-flight NotePart. Insertion paths with no clipboard to read from supply the
 *      equivalent map through `NoteLinkedGroupPlugin.stashContainedServerIdsForPaste`.
 *   2. The group's paste converter (`convertNotePartGridForPaste` for image grids,
 *      `convertStoichiometryGroupForPaste` for reactions) `take`s it back out (which also strips
 *      it) and uses it as a fallback when the destination entry's `getIdFromCid` can't resolve a
 *      contained file.
 *
 * The field is underscore-prefixed and deliberately NOT part of the NotePart shape: it must never
 * reach the Redux store or serialization, which is why `take` always removes it. Centralizing the
 * key and the (un)wrapping here (the shared note-linked-group constants module, not any single
 * group's constants) keeps that contract — and the otherwise-stringly-typed magic key — in one
 * place instead of duplicated, untyped casts across the consumers.
 */
interface NotePartWithContainedServerIdsFromClipboard {
  _containedServerIdsFromClipboard?: ContainedServerIdsFromClipboard;
}

export function stashContainedServerIdsFromClipboard(
  notePart: NotePart,
  containedServerIds: ContainedServerIdsFromClipboard
): void {
  (notePart as NotePartWithContainedServerIdsFromClipboard)._containedServerIdsFromClipboard =
    containedServerIds;
}

/**
 * Reads and removes the transient clipboard id map from `notePart`. Always strips the field (even
 * when absent) so it can never be persisted, and returns `undefined` when no map was stashed.
 */
export function takeContainedServerIdsFromClipboard(
  notePart: NotePart
): ContainedServerIdsFromClipboard | undefined {
  const carrier = notePart as NotePartWithContainedServerIdsFromClipboard;
  const containedServerIds = carrier._containedServerIdsFromClipboard;
  delete carrier._containedServerIdsFromClipboard;
  return containedServerIds;
}
